import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useUsage } from '../../context/UsageContext';
import { hasReachedLimit } from '../../services/usageService';
import { customerPath, projectPath, QUICK_CREATE_EVENT, templateEditorPath } from '../../utils/navigation';

/** Same limit the Customers page enforces. */
const CUSTOMER_NAME_MAX_LENGTH = 120;

/**
 * Validate an optional website: http(s) URLs or bare domains only.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or '' when valid or empty.
 */
export function websiteError(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (!/^https?:\/\//i.test(trimmed) && /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return 'Website must use http:// or https://';
  const host = trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split(/[/?#]/)[0].toLowerCase();
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host) ? '' : 'Enter a valid website (e.g. https://example.com)';
}

/**
 * Normalize a valid website for saving (bare domains get https://).
 * @param {string} value - Raw input.
 * @returns {string} URL or ''.
 */
export function normalizeWebsite(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * Shell-level quick create, opened from the sidebar "New" menu, the command palette or any page
 * via `requestQuickCreate(kind, options)` (utils/navigation):
 * - project: customer + name + description, then opens the new project;
 * - customer: name + website + description, then opens the new customer (contacts and email
 *   domains are edited there); started from the project dialog it returns to it instead;
 * - template: checks the plan's template limit (like the Templates page) before opening the
 *   template editor, and offers an upgrade when the limit is reached.
 * @returns {JSX.Element} Dialogs.
 */
export default function QuickCreateHost() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { state: appState, actions: appActions } = useApp();
  const { actions: authActions } = useAuth();
  const { refreshUsage } = useUsage();

  const [kind, setKind] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', customerId: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  /** Set when the customer dialog was opened from the project dialog (return there after). */
  const returnToProjectRef = useRef(false);
  const customersRef = useRef(appState.customers);
  customersRef.current = appState.customers;
  const busyRef = useRef(false);

  const close = useCallback(() => {
    if (busyRef.current) return;
    returnToProjectRef.current = false;
    setKind(null);
    setError('');
  }, []);

  /**
   * Check the template quota, then open the template editor (or the limit dialog).
   * @param {string} templateKind - 'document' | 'powerpoint-rich'.
   * @returns {Promise<void>}
   */
  const startTemplate = useCallback(async (templateKind) => {
    if (!authActions.canManageTemplates()) return;
    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'templates')) {
      setLimitReached(true);
      return;
    }
    navigate(templateEditorPath({ templateId: 'new', kind: templateKind || 'document' }));
  }, [authActions, refreshUsage, navigate]);

  useEffect(() => {
    const onRequest = (event) => {
      const detail = event.detail || {};
      if (detail.kind === 'template') {
        startTemplate(detail.templateKind);
        return;
      }
      if (detail.kind !== 'project' && detail.kind !== 'customer') return;
      if (busyRef.current) return;
      const customers = customersRef.current || [];
      const customerId = customers.some((c) => c.id === detail.customerId)
        ? detail.customerId
        : customers.length === 1 ? customers[0].id : '';
      returnToProjectRef.current = false;
      setForm({ name: '', description: '', customerId, website: '' });
      setError('');
      setKind(detail.kind);
    };
    window.addEventListener(QUICK_CREATE_EVENT, onRequest);
    return () => window.removeEventListener(QUICK_CREATE_EVENT, onRequest);
  }, [startTemplate]);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  /**
   * Run a create action with busy/error handling.
   * @param {() => Promise<{success: boolean, data?: object, error?: string}>} run - Create call.
   * @param {(data: object) => void} onDone - Called with the created row.
   * @returns {Promise<void>}
   */
  const submit = async (run, onDone) => {
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await run();
      if (!result?.success || !result.data?.id) {
        setError(result?.error || 'Something went wrong. Please try again.');
        return;
      }
      onDone(result.data);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const createProject = () => {
    if (!form.name.trim() || !form.customerId) return;
    submit(
      () => appActions.createProject({ name: form.name.trim(), description: form.description.trim(), customer_id: form.customerId }),
      (project) => {
        setKind(null);
        navigate(projectPath(project.id));
      }
    );
  };

  const siteError = websiteError(form.website);
  const createCustomer = () => {
    if (!form.name.trim() || siteError) return;
    submit(
      () => appActions.createCustomer({
        name: form.name.trim(),
        description: form.description.trim(),
        company_website: normalizeWebsite(form.website),
        domain: [],
      }),
      (customer) => {
        if (returnToProjectRef.current) {
          returnToProjectRef.current = false;
          setForm({ name: '', description: '', customerId: customer.id, website: '' });
          setKind('project');
          return;
        }
        setKind(null);
        navigate(customerPath(customer.id));
      }
    );
  };

  const switchToNewCustomer = () => {
    returnToProjectRef.current = true;
    setForm((prev) => ({ ...prev, name: '', description: '', website: '' }));
    setError('');
    setKind('customer');
  };

  const customers = appState.customers || [];
  const onSubmit = (event) => {
    event.preventDefault();
    if (kind === 'project') createProject();
    else if (kind === 'customer') createCustomer();
  };

  return (
    <>
      <Dialog
        open={kind === 'project' || kind === 'customer'}
        onClose={close}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{ component: 'form', onSubmit, noValidate: true, 'aria-labelledby': 'quick-create-title' }}
      >
        <DialogTitle id="quick-create-title">{kind === 'customer' ? 'New customer' : 'New project'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {kind === 'project' && (
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              {customers.length === 0 ? (
                <Alert
                  severity="info"
                  action={<Button color="inherit" size="small" onClick={switchToNewCustomer}>New customer</Button>}
                >
                  Projects belong to a customer. Create one first.
                </Alert>
              ) : (
                <FormControl fullWidth required>
                  <InputLabel id="quick-create-customer">Customer</InputLabel>
                  <Select
                    labelId="quick-create-customer"
                    label="Customer"
                    value={form.customerId}
                    onChange={setField('customerId')}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField autoFocus required label="Project name" fullWidth value={form.name} onChange={setField('name')} />
              <TextField label="Description (optional)" fullWidth multiline minRows={3} value={form.description} onChange={setField('description')} />
            </Box>
          )}
          {kind === 'customer' && (
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              <TextField
                autoFocus
                required
                label="Customer name"
                fullWidth
                value={form.name}
                onChange={setField('name')}
                inputProps={{ maxLength: CUSTOMER_NAME_MAX_LENGTH }}
                helperText={form.name.length >= CUSTOMER_NAME_MAX_LENGTH - 20 ? `${form.name.length}/${CUSTOMER_NAME_MAX_LENGTH} characters` : undefined}
              />
              <TextField
                label="Company website (optional)"
                type="url"
                fullWidth
                value={form.website}
                onChange={setField('website')}
                error={Boolean(siteError)}
                helperText={siteError || 'Contacts and email domains can be added on the customer page.'}
              />
              <TextField label="Description (optional)" fullWidth multiline minRows={2} value={form.description} onChange={setField('description')} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !form.name.trim() || (kind === 'project' ? !form.customerId : Boolean(siteError))}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {kind === 'customer' ? 'Create customer' : 'Create project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limitReached} onClose={() => setLimitReached(false)} aria-labelledby="quick-create-limit-title">
        <DialogTitle id="quick-create-limit-title">Template limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've reached your template limit. Upgrade your plan to create more templates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReached(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLimitReached(false);
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
